import isEmpty from 'lodash/isEmpty';
import parseISO from 'date-fns/parseISO';
import toDate from 'date-fns/toDate';
import { statusCategoryForId } from '@jira/platform__status-categories/src/status-categories.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	RESPONDERS_CF_TYPE,
	SERVICE_ENTITY_CF_TYPE,
	TEXT_AREA_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	ASSIGNEE_CHANGED,
	ATTACHMENT_CHANGED,
	ATTACHMENT_DELETED,
	ATTACHMENT_UPLOADED,
	COMMENT_CHANGED,
	FIELD_CHANGED,
	type HistoryItem,
	ISSUE_CREATED,
	type IssueCreatedHistoryItem,
	NUMERIC_FIELD_CHANGED,
	RICH_TEXT_FIELD_CHANGED,
	PARENT_ISSUE_CHANGED,
	PRIORITY_CHANGED,
	STATUS_CHANGED,
	TIME_CHANGED,
	type User,
	WORK_LOGGED,
	DATE_ISSUE_CHANGED,
	RESPONDERS_CHANGED,
	AFFECTED_SERVICES_CHANGED,
	ARCHIVED_CHANGED,
	DATA_CLASSIFICATION_CHANGED,
	DATA_REDACTED,
	DATA_REDACTION_RESTORED,
} from '../../../types.tsx';
import type { IssueHistoryResult } from '../types.tsx';
import type {
	ActorGraphQlResponse,
	ActorRestResponse,
	HistoryItemGraphQlResponse,
	GraphQlData,
	CreatedInfoResponse,
} from './types.tsx';

const transformRestActor = (actor?: ActorRestResponse): User | null =>
	actor
		? {
				avatarUrl: actor.avatarUrls['48x48'],
				displayName: actor.displayName,
				accountId: actor.accountId,
			}
		: null;

const transformGraphQlActor = (actor?: ActorGraphQlResponse): User | null =>
	actor
		? {
				avatarUrl: actor.avatarUrls.large,
				displayName: actor.displayName,
				accountId: actor.accountId,
			}
		: null;

export const transformChangelogItem = (item: HistoryItemGraphQlResponse): HistoryItem => {
	const {
		fieldSchema,
		fieldId,
		fieldType,
		fieldDisplayName,
		timestamp: changelogTimestamp,
		actor: changelogActor,
		isDueToRedaction,
		isDueToRedactionRestore,
	} = item;

	// so we don't need `if (from)...` on every branch
	let { from, to } = item;
	// @ts-expect-error - TS2322 - Type 'FieldValueGraphQlResponse | {}' is not assignable to type 'FieldValueGraphQlResponse | null | undefined'.
	from = from || {};
	// @ts-expect-error - TS2322 - Type 'FieldValueGraphQlResponse | {}' is not assignable to type 'FieldValueGraphQlResponse | undefined'.
	to = to || {};

	const actor = transformGraphQlActor(changelogActor);
	const timestamp =
		typeof changelogTimestamp === 'string'
			? parseISO(changelogTimestamp)
			: toDate(changelogTimestamp);

	if (fg('enable_redaction_support_in_jira_issue_view')) {
		if (isDueToRedaction) {
			return {
				fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
					? fieldType
					: undefined,
				type: DATA_REDACTED,
				actor,
				timestamp,
				field: fieldDisplayName,
			};
		}

		if (isDueToRedactionRestore && fg('enable_redaction_restoration_history_view')) {
			return {
				fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
					? fieldType
					: undefined,
				type: DATA_REDACTION_RESTORED,
				actor,
				timestamp,
				field: fieldDisplayName,
				// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
				fromValue: from?.displayValue,
				// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
				toValue: to?.displayValue,
			};
		}
	}

	if (
		((fieldType === 'jira' && fieldId === 'description') ||
			(fieldType === 'custom' && fieldSchema?.customFieldType === TEXT_AREA_CF_TYPE)) &&
		expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
	) {
		return {
			fieldType,
			type: RICH_TEXT_FIELD_CHANGED,
			actor,
			timestamp,
			field: fieldDisplayName,
			// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
			fromValue: from?.displayValue,
			// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
			toValue: to?.displayValue,
		};
	}

	if (fieldType === 'jira' && fieldId === 'assignee') {
		return {
			fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
				? fieldType
				: undefined,
			type: ASSIGNEE_CHANGED,
			actor,
			timestamp,
			// @ts-expect-error - TS2322 - Type '{ displayName: string | null; avatarUrl: string | undefined; accountId: string | undefined; } | null' is not assignable to type 'User | null'. | TS2533 - Object is possibly 'null' or 'undefined'.
			fromUser: !isEmpty(from.displayValue)
				? {
						// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
						displayName: from.displayValue,
						// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
						avatarUrl: from.avatarUrl,
						// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
						accountId: from.accountId,
					}
				: null,
			// @ts-expect-error - TS2322 - Type '{ displayName: string | null; avatarUrl: string | undefined; accountId: string | undefined; } | null' is not assignable to type 'User | null'. | TS2532 - Object is possibly 'undefined'.
			toUser: !isEmpty(to.displayValue)
				? {
						// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
						displayName: to.displayValue,
						// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
						avatarUrl: to.avatarUrl,
						// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
						accountId: to.accountId,
					}
				: null,
		};
	}
	if (fieldType === 'jira' && fieldId === 'status') {
		return {
			fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
				? fieldType
				: undefined,
			type: STATUS_CHANGED,
			actor,
			timestamp,
			// @ts-expect-error - TS2322 - Type '{ name: string | null; statusCategory: StatusCategory; } | null' is not assignable to type 'Status | null'. | TS2533 - Object is possibly 'null' or 'undefined'.
			fromStatus: !isEmpty(from.displayValue)
				? {
						// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
						name: from.displayValue,
						// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
						statusCategory: statusCategoryForId(from.categoryId),
					}
				: null,
			// @ts-expect-error - TS2322 - Type '{ name: string | null; statusCategory: StatusCategory; } | null' is not assignable to type 'Status | null'. | TS2532 - Object is possibly 'undefined'.
			toStatus: !isEmpty(to.displayValue)
				? {
						// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
						name: to.displayValue,
						// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
						statusCategory: statusCategoryForId(to.categoryId),
					}
				: null,
		};
	}
	if (fieldType === 'jira' && fieldId === 'comment') {
		return {
			fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
				? fieldType
				: undefined,
			type: COMMENT_CHANGED,
			actor,
			timestamp,
		};
	}
	if (fieldType === 'jira' && fieldId === 'softArchived') {
		return {
			fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
				? fieldType
				: undefined,
			type: ARCHIVED_CHANGED,
			actor,
			timestamp,
			fromValue: from?.value,
		};
	}
	if (fieldType === 'jira' && fieldId === 'attachment') {
		return {
			fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
				? fieldType
				: undefined,
			type: ATTACHMENT_CHANGED,
			actor,
			timestamp,
			// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'. | TS2532 - Object is possibly 'undefined'.
			filename: from.displayValue ?? to.displayValue ?? '',
			// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
			action: !isEmpty(from.displayValue) ? ATTACHMENT_DELETED : ATTACHMENT_UPLOADED,
		};
	}
	if (fieldType === 'jira' && fieldId === 'priority') {
		return {
			fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
				? fieldType
				: undefined,
			type: PRIORITY_CHANGED,
			actor,
			timestamp,
			fromPriority: {
				// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
				iconUrl: from.iconUrl || '',
				// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
				name: from.displayValue || '',
			},
			toPriority: {
				// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
				iconUrl: to.iconUrl || '',
				// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
				name: to.displayValue || '',
			},
		};
	}
	// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
	if (fieldType === 'jira' && fieldDisplayName === 'WorklogId' && to.worklog) {
		return {
			fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
				? fieldType
				: undefined,
			type: WORK_LOGGED,
			actor,
			timestamp,
			// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
			comment: to.worklog.comment,
			// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
			timeSpent: to.worklog.timeSpent,
		};
	}
	if (fieldType === 'jira' && fieldDisplayName === 'IssueParentAssociation') {
		return {
			fieldType,
			type: PARENT_ISSUE_CHANGED,
			actor,
			timestamp,
			// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
			fromValue: from.displayValue,
			// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
			toValue: to.displayValue,
		};
	}
	if (
		fieldType === 'jira' &&
		(fieldId === 'timespent' || fieldId === 'timeestimate' || fieldId === 'timeoriginalestimate')
	) {
		return {
			fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
				? fieldType
				: undefined,
			type: TIME_CHANGED,
			actor,
			field: fieldDisplayName,
			timestamp,
			timeTrackingConfig: item.timeTrackingConfig ? item.timeTrackingConfig : null,
			// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'. | TS2345 - Argument of type 'string | null' is not assignable to parameter of type 'string'.
			fromTime: parseInt(from.value, 10),
			// @ts-expect-error - TS2532 - Object is possibly 'undefined'. | TS2345 - Argument of type 'string | null' is not assignable to parameter of type 'string'.
			toTime: parseInt(to.value, 10),
		};
	}
	if (
		fieldType === 'custom' &&
		fieldSchema &&
		fieldSchema.customFieldType === 'com.atlassian.jira.plugin.system.customfieldtypes:float'
	) {
		return {
			fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
				? fieldType
				: undefined,
			type: NUMERIC_FIELD_CHANGED,
			actor,
			timestamp,
			field: fieldDisplayName,
			// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'. | TS2533 - Object is possibly 'null' or 'undefined'.
			fromValue: !isEmpty(from.displayValue) ? Number(from.displayValue) : null,
			// @ts-expect-error - TS2532 - Object is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'.
			toValue: !isEmpty(to.displayValue) ? Number(to.displayValue) : null,
		};
	}

	if (
		((fieldType === 'jira' || fieldType === 'custom') &&
			fieldSchema &&
			fieldSchema.type === 'date') ||
		(fieldType === 'custom' && fieldSchema && fieldSchema.type === 'datetime')
	) {
		// handles the default due date and custom date/datetime fields for the history log.
		return {
			fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
				? fieldType
				: undefined,
			type: DATE_ISSUE_CHANGED,
			actor,
			timestamp,
			field: fieldDisplayName,
			// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
			fromDate: from.value,
			// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
			toDate: to.value,
			fieldSchema: fieldSchema.type,
		};
	}
	if (fieldType === 'custom' && fieldSchema?.customFieldType === RESPONDERS_CF_TYPE) {
		return {
			fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
				? fieldType
				: undefined,
			type: RESPONDERS_CHANGED,
			actor,
			timestamp,
			field: fieldDisplayName,
			// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
			fromValue: from.responders || null,
			// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
			toValue: to.responders || null,
		};
	}

	if (fieldType === 'custom' && fieldSchema?.customFieldType === SERVICE_ENTITY_CF_TYPE) {
		return {
			fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
				? fieldType
				: undefined,
			type: AFFECTED_SERVICES_CHANGED,
			actor,
			timestamp,
			field: fieldDisplayName,
			fromValue: from?.value || null,
			toValue: to?.value || null,
			fromDisplayValue: from?.displayValue || null,
			toDisplayValue: to?.displayValue || null,
		};
	}

	if (fieldType === 'jira' && fieldId === 'dataClassification') {
		return {
			fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
				? fieldType
				: undefined,
			type: DATA_CLASSIFICATION_CHANGED,
			actor,
			timestamp,
			field: fieldDisplayName,
			fromClassification: from?.displayValue
				? {
						name: from?.displayValue,
						// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
						color: from?.color,
						// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
						guidelines: from?.guidelines,
					}
				: null,
			toClassification: to?.displayValue
				? {
						name: to?.displayValue,
						// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
						color: to?.color,
						// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
						guidelines: to?.guidelines,
					}
				: null,
		};
	}

	if (fieldType === 'jira' && fieldId === 'created' && fg('relay-migration-issue-history')) {
		return {
			fieldType: 'jira',
			type: ISSUE_CREATED,
			actor,
			timestamp,
		};
	}

	return {
		fieldType: expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)
			? fieldType
			: undefined,
		type: FIELD_CHANGED,
		actor,
		timestamp,
		field: fieldDisplayName,
		// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
		fromValue: from.displayValue,
		// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
		toValue: to.displayValue,
	};
};

export const transformCreateInfoResponse = ({
	fields: { created, creator },
}: CreatedInfoResponse): IssueCreatedHistoryItem => ({
	fieldType: 'jira',
	type: ISSUE_CREATED,
	actor: transformRestActor(creator),
	timestamp: parseISO(created),
});

export const transformGraphQlResponse = (
	data: GraphQlData,
	maxResults: number,
): IssueHistoryResult => ({
	nextPageStartIndex: data.viewIssue.history.isLast
		? 0
		: data.viewIssue.history.startIndex + maxResults,
	isLastPage: data.viewIssue.history.isLast,
	history: data.viewIssue.history.nodes.map(transformChangelogItem),
});
